// Holds the reading position across a form submission.
//
// A form that posts and comes back is a full page navigation, so the browser puts the reader at the
// top — always, in every browser. On a short form nobody notices; on a long one, every submission
// throws you away from the control you just used, and you have to find your way back down to see
// what your own action did. The editor of the form tool met this first and grew its own fix; the
// same jump happens on every screen that posts and re-renders, so the fix belongs here instead.
//
// The position is saved on the way out and put back on the way in. Two decisions carry the whole
// module:
//
//   1. It is only put back WHERE IT MEANS SOMETHING. A saved offset is meaningless on a different
//      page — restoring it there would scroll the reader into the middle of something they have
//      never seen. So the landing page has to be either the page we left, or the address the form
//      posted to. Anything else (a create that redirects to the new resource, a login that lands on
//      a console) keeps the browser's own behaviour, which is to start at the top.
//
//   2. It never fights an explicit destination. A fragment in the URL already says where to go, and
//      it wins.
//
// Progressive: with JavaScript off, forms behave as they always did. What is lost is the held
// position, not the submission.

const KEY = 'arcate-hold-position';

/** The part of an address that decides whether two pages are the same page. */
function addressOf(url) {
	return url.pathname + url.search;
}

/** Where this submission is going, honouring a button that overrides the form's action. */
function destinationOf(form, submitter) {
	const action = (submitter && submitter.getAttribute('formaction')) || form.getAttribute('action');
	try {
		return addressOf(new URL(action || window.location.href, window.location.href));
	} catch (ignored) {
		// A malformed action is the server's problem, not ours: we simply hold nothing.
		return null;
	}
}

// Bubble phase on the document, deliberately: it runs AFTER any listener the form itself carries,
// which is what lets `defaultPrevented` tell us the submission was taken over (an AJAX form, an
// editor doing its own thing). Those never navigate, so there is no position to hold.
document.addEventListener('submit', (event) => {
	const form = event.target;
	if (!(form instanceof HTMLFormElement)) return;
	if (event.defaultPrevented) return;
	if (form.hasAttribute('data-ajax')) return;
	if (form.dataset.holdPosition === 'off') return;

	// GET forms are searches and filters: they land on a different query, so the page that comes
	// back is not the page we left, and starting at the top is the right answer.
	if ((form.getAttribute('method') || 'get').toLowerCase() !== 'post') return;

	// A submission that opens elsewhere leaves this page exactly as it is — nothing to hold.
	const target = (event.submitter && event.submitter.getAttribute('formtarget')) || form.getAttribute('target');
	if (target && target !== '_self') return;

	const to = destinationOf(form, event.submitter);
	if (to === null) return;

	try {
		window.sessionStorage.setItem(KEY, JSON.stringify({
			y: Math.round(window.scrollY),
			from: addressOf(window.location),
			to,
		}));
	} catch (ignored) {
		// Private browsing can refuse storage. A jumping page is a nuisance, not a failure.
	}
});

(function restore() {
	let raw;
	try {
		raw = window.sessionStorage.getItem(KEY);
		// Consumed on read, and that matters: without it, a later ordinary navigation to the same
		// address would be scrolled by a position the reader never asked for.
		window.sessionStorage.removeItem(KEY);
	} catch (ignored) {
		return;
	}
	if (!raw) return;
	if (window.location.hash) return;

	let saved;
	try {
		saved = JSON.parse(raw);
	} catch (ignored) {
		return;
	}
	if (!saved || typeof saved.y !== 'number' || saved.y <= 0) return;

	const here = addressOf(window.location);
	if (here !== saved.from && here !== saved.to) return;

	// `instant`, and not the two-argument form: the themes set `scroll-behavior: smooth` on <html>,
	// which would turn the restore into a visible journey down the page on every submission.
	// Putting a position back is not a movement the reader made — it should not be animated at all.
	window.scrollTo({ top: saved.y, behavior: 'instant' });
	// Again once everything has loaded: a web font swapping in or an image arriving changes the
	// height under us, and the first call would have landed short.
	window.addEventListener('load', () => window.scrollTo({ top: saved.y, behavior: 'instant' }),
		{ once: true });
}());
